import { useState } from 'react';
import { Modal, ModalContent } from '@nextui-org/react';
import { useTranslation } from 'react-i18next';
import { Hourglass } from 'lucide-react';
import { toast } from 'react-toastify';

import { trackEvent } from '../utils/gt';

const API_URL = import.meta.env.VITE_GRAPHQL_URL.replace('/graphql', '');

export const ModalTrialRestriction = ({ isOpen, onOpenChange, onClose, title, description, onSkipTrialSuccess, isCanceled }) => {
  const { t } = useTranslation();
  const [isSkipping, setIsSkipping] = useState(false);
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);

  const handleManageSubscription = async () => {
    trackEvent(
      'modal_trial_restriction_manage_subscription',
      'Modal Trial Restriction - Manage Subscription',
      'modal_trial_restriction',
      true
    );
    setIsLoadingPortal(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/subscriptions/portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ returnUrl: window.location.href }),
      });
      const { url } = response.ok ? await response.json() : {};
      if (!url) throw new Error('No portal url');
      window.location.href = url;
    } catch {
      // Never leave the button spinning: say it failed and let the user try again
      setIsLoadingPortal(false);
      toast.error(t('notifications.subscriptionPortalError'), { toastId: 'subscription-portal-error' });
    }
  };

  const handleSkipTrial = async () => {
    trackEvent(
      'modal_trial_restriction_skip_trial',
      'Modal Trial Restriction - Skip Trial',
      'modal_trial_restriction',
      true
    );
    setIsSkipping(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/subscriptions/skip-trial`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to skip trial');
      }

      if (onSkipTrialSuccess) {
        await onSkipTrialSuccess();
      }
      toast.success(t('components.ModalTrialRestriction.skipSuccess'), {
        toastId: 'skip-trial-success',
        autoClose: 3000,
      });
      onClose();
    } catch {
      toast.error(t('components.ModalTrialRestriction.skipError'), {
        toastId: 'skip-trial-error',
      });
    } finally {
      setIsSkipping(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      placement="center"
      hideCloseButton
      classNames={{ base: 'mt-root mt-dialog mt-promo', backdrop: 'mt-dialog__backdrop' }}
    >
      <ModalContent>
        {() => (
          <div className="mt-dialog__body mt-promo__body">
            <span className="mt-promo__seal" aria-hidden="true">
              <Hourglass />
            </span>
            <h2 className="mt-dialog__title">
              {title || t(isCanceled ? 'components.ModalTrialRestriction.canceledTitle' : 'components.ModalTrialRestriction.title')}
              <span className="mt-promo__badge">{t('components.ModalTrialRestriction.badge')}</span>
            </h2>
            <p className="mt-dialog__text">
              {description || t(isCanceled ? 'components.ModalTrialRestriction.canceledDesc' : 'components.ModalTrialRestriction.desc')}
            </p>
            <div className="mt-promo__actions">
              {isCanceled ? (
                <button type="button" className="mt-btn mt-btn--primary" onClick={handleManageSubscription} disabled={isLoadingPortal} aria-busy={isLoadingPortal || undefined}>
                  {isLoadingPortal && <span className="mt-btn__spinner" aria-hidden="true" />}
                  {t('components.ModalTrialRestriction.manageSub')}
                </button>
              ) : (
                <button type="button" className="mt-btn mt-btn--primary" onClick={handleSkipTrial} disabled={isSkipping} aria-busy={isSkipping || undefined}>
                  {isSkipping && <span className="mt-btn__spinner" aria-hidden="true" />}
                  {t('components.ModalTrialRestriction.skipTrialCta')}
                </button>
              )}
              <button
                type="button"
                className="mt-btn mt-btn--quiet"
                disabled={isSkipping || isLoadingPortal}
                onClick={() => {
                  trackEvent('modal_trial_restriction_dismiss', 'Modal Trial Restriction - Dismiss', 'modal_trial_restriction', true);
                  onClose();
                }}
              >
                {t('components.ModalTrialRestriction.dismiss')}
              </button>
            </div>
          </div>
        )}
      </ModalContent>
    </Modal>
  );
};
